/**
 * Conversations — the dialogue primitive of the support domain triad:
 * feedback = a signal (user speaks once) · issue = a work item (team decides) ·
 * conversation = a dialogue (user ↔ support-concierge bot ↔ human escalation).
 *
 * All the "enums" here are string unions validated in the app layer (NOT pg enums), so future
 * kinds are zero-migration. Server is the only writer; mobile + admin consume these types.
 */
import { type } from "arktype";
import { RooDate } from "roo-time";
export const CONVERSATION_STATUSES = ["open", "resolved"];
/**
 * Who replies next — orthogonal to `status`. New conversations start `bot` (the bot is the
 * always-on first responder); an admin claim moves to `human` (the bot falls silent); release
 * hands back to `bot`. Since escalation v2 (2026-06-12), a HUMAN REQUEST is a flag
 * (`human_requested_at`) — owner stays `bot` and the bot keeps answering until the claim;
 * `queued_human` is reserved for OUTAGE routing (states where the bot can't reply at all).
 */
export const CONVERSATION_OWNERS = ["bot", "queued_human", "human"];
/**
 * The actor taxonomy (repo-wide, four kinds — each domain uses the ones that apply):
 *   "user"  = an end user in the mobile app.
 *   "admin" = an internal human acting through the admin portal. Identity, NOT privilege —
 *             capability is governed by scopes (a conversations-only support hire and a
 *             full-access founder are both "admin").
 *   "bot"   = the product's user-facing support assistant (Roo Assistant) — this domain's AI.
 *   "agent" = an autonomous coding/workflow AI on a devpod (e.g. agent:claude@kevin) working
 *             the issue tracker — NOT a conversations author; listed so the bot/agent
 *             distinction is recorded exactly once.
 */
export const CONVERSATION_AUTHOR_KINDS = ["user", "admin", "bot"];
/**
 * Lifecycle actors = the author kinds + "system" (the platform itself — currently only the idle
 * auto-close sweep). "system" may stamp `resolved_by_kind`, handoff audit rows, and author
 * `system_note` rows, but it is never a conversational author of `text` messages — it isn't a
 * fifth participant in the taxonomy above, just machine-initiated lifecycle bookkeeping.
 */
export const CONVERSATION_LIFECYCLE_ACTOR_KINDS = [...CONVERSATION_AUTHOR_KINDS, "system"];
/** The actor id stamped alongside actor/author kind "system". */
export const CONVERSATION_SYSTEM_ACTOR_ID = "system";
/**
 * "handoff_notice" (escalation v2): the "<Name> joined the chat" row appended when an admin
 * claims — clients render it as the joined CARD (avatar + name + role subtitle); anything
 * that doesn't know the kind falls back to centered note text (the body reads fine plain).
 */
export const CONVERSATION_MESSAGE_KINDS = ["text", "system_note", "handoff_notice"];
/** Bot rows are born `streaming` (placeholder while tokens flow) and finalize to `complete`/`failed`. */
export const CONVERSATION_MESSAGE_STATUSES = ["complete", "streaming", "failed"];
export const CONVERSATION_HANDOFF_EVENT_KINDS = ["queue", "claim", "release", "resolve", "reopen"];
/**
 * Longest conversation-tag label the picker renders without truncating an inbox row's chip (T-334).
 *
 * Lives HERE rather than in roo-server so the admin SPA can enforce it on the input. The SPA imports
 * only *types* from roo-server, so while this constant lived there the only options were to duplicate
 * the number (two copies, free to drift) or to let the admin type past the bound and learn about it
 * from a round-tripped `INVALID_LABEL`. The server's `validateTagLabel` remains the authority — this
 * is what lets the client stop the mistake before it costs a request.
 */
export const TAG_LABEL_MAX_LENGTH = 40;
/**
 * The tag dedup key: lowercase, non-alphanumerics collapsed to a single "-", trimmed. "Billing
 * Issues", "billing issues" and "BILLING--ISSUES!" all normalize to `billing-issues`, which is what
 * stops one theme being minted three times.
 *
 * ⚠ ASCII-ONLY IS THE VOCABULARY POLICY, not an oversight. `[^a-z0-9]` strips every non-ASCII letter,
 * so a label with nothing else in it (CJK, Cyrillic, Greek) normalizes to "" and the server refuses
 * it as INVALID_LABEL; diacritics split themes ("Café" → `caf`, "Cafe" → `cafe`). The catalog is
 * internal team vocabulary written in English; widening this to Unicode is a deliberate change (NFD
 * -fold the diacritics, allow `\p{L}\p{N}`), not a bug fix.
 *
 * Shared rather than server-only because a tag's chip COLOR is a pure function of its slug, and the
 * slug tracks the label — so the admin SPA has to be able to derive "what colour will this become?"
 * to show an honest rename preview. The server's `validateTagLabel` stays the authority on whether a
 * label is acceptable; this is only the normalization both sides must agree on.
 */
export function normalizeTagSlug(label) {
    return label
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
}
// ---------------------------------------------------------------------------
// Support mode — ONE global enum (GlobalSettings.conversations.supportMode) deciding what
// support looks like for everyone with `enable_conversations` on. The flag is the only
// per-user targeting lever; the enum is the only "what kind of support" lever.
// ---------------------------------------------------------------------------
/**
 * "form"          → conversations OFF for everyone (the feedback form, as if the flag were off);
 * "bot_only"      → the bot answers; no human escalation anywhere (honest dead-ends, no queue);
 * "human_only"    → no LLM turns; every thread routes to the human queue;
 * "bot_and_human" → the full system (bot first, humans reachable). The default.
 */
export const CONVERSATION_SUPPORT_MODES = ["form", "bot_only", "human_only", "bot_and_human"];
/**
 * Fail-OPEN sanitizer for the RTDB-sourced value: missing/unknown reads as the full system,
 * so a typo'd publish can never silently turn support off. Shared by server resolution and
 * the mobile gate so the two can never disagree.
 */
export function sanitizeSupportMode(value) {
    return CONVERSATION_SUPPORT_MODES.includes(value)
        ? value
        : "bot_and_human";
}
/** Does this mode ever let the BOT take a turn? (Key custody still applies on top.) */
export function supportModeAllowsBot(mode) {
    return mode === "bot_only" || mode === "bot_and_human";
}
export const HUMAN_REPLY_ONLINE_COPY = "A teammate is online now.";
export const HUMAN_REPLY_OFFLINE_COPY = "No one's online right now — a team member usually responds within 24 hours.";
/** The expectation for a given presence read — pure, shared so server + tests can't drift. */
export function composeHumanReplyExpectation(p) {
    return { online: p.online, copy: p.online ? HUMAN_REPLY_ONLINE_COPY : HUMAN_REPLY_OFFLINE_COPY };
}
// ---------------------------------------------------------------------------
// Escalation v2 (human_requested) — asking for a human no longer silences the bot: the
// conversation stamps human_requested_at, owner stays "bot", and the bot keeps answering
// until an admin claims (the "joined the chat" handoff_notice). queued_human is reserved
// for outage routing. Copy is composed server-side once so clients can't phrase-drift.
// ---------------------------------------------------------------------------
/** The visible note appended when a human is requested (user tap or the bot's handoff tool). */
export const HUMAN_REQUESTED_NOTE_BODY = "You asked for a human teammate — if someone's available, they'll join this conversation. In the meantime, feel free to keep working with Roo Assistant.";
/**
 * The visible note appended when a human is re-requested on a thread a human has ALREADY handled
 * (e.g. it was released back to the bot, then the user asked for a person again). Unlike the
 * escalation-v2 flag note above, the bot steps aside here — the thread routes back to the human
 * queue — so this copy must NOT invite the user to keep working with Roo Assistant. See T-168.
 */
export const QUEUED_FOR_HUMAN_NOTE_BODY = "You're back in line for a teammate — they'll reply right here. Roo Assistant has stepped aside for now.";
/** The waiting-state banner label (mobile), straight from the mock. */
export const CONNECTING_TO_TEAM_BANNER_LABEL = "Connecting you to the Roo team…";
/** The joined card's role subtitle — fixed copy for now (admin_user has no role field). */
export const HUMAN_JOINED_SUBTITLE = "Roo Support Specialist";
/** The handoff_notice body ("Kevin joined the chat") — falls back readable as plain text. */
export function buildHumanJoinedNoteBody(p) {
    return `${p.displayName} joined the chat`;
}
/**
 * Can a HUMAN end up replying in a thread under this mode? False only for bot_only — "form"
 * and "human_only" keep humans reachable for threads that already exist (admins still work
 * the inbox even while new entry points are closed).
 */
export function supportModeAllowsHumans(mode) {
    return mode !== "bot_only";
}
// ---------------------------------------------------------------------------
// Multi-conversation model — a user may hold several open threads at once; threads are
// browsable from a Support inbox and reopenable for a window after resolve.
// ---------------------------------------------------------------------------
/**
 * How long after `resolved_at` a USER message may revive the thread (status → open,
 * owner → bot so it re-triages). Past the window the thread is locked: read-only history,
 * "start a new conversation" CTA. Admin reopens (`setStatus`) are NOT window-bound.
 */
export const CONVERSATION_REOPEN_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
/** Pure reopen-window check — shared so mobile's pre-check can't drift from the server gate. */
export function canReopenConversation(p) {
    return p.nowMs - p.resolvedAtMs <= CONVERSATION_REOPEN_WINDOW_MS;
}
/**
 * The intent-chip opener ("What do you want to talk about?") on a NEW conversation. Stored on
 * `conversation.intent`, fed to the bot's context — deliberately NOT an admin-facing chip.
 * The chip's label is persisted as the user's first message; the follow-up below is appended
 * as a scripted (non-LLM) bot message so the preamble costs nothing and works identically in
 * humans-only mode.
 */
export const CONVERSATION_INTENTS = ["support_bug", "feedback", "other"];
export const CONVERSATION_INTENT_CHIP_LABELS = {
    support_bug: "Support / Bug",
    feedback: "Feedback",
    other: "Other",
};
export const CONVERSATION_INTENT_FOLLOW_UPS = {
    support_bug: "What's going on? Tell us what happened.",
    feedback: "We'd love to hear it — what's your idea?",
    other: "Go ahead — what's on your mind?",
};
/**
 * The user-facing inbox row state, derived (never stored) from `status` + who authored the
 * newest TEXT message. The admin's `awaitingReply` is the same computation read from the other
 * side: user "waiting_on_team" ⇔ admin "user is waiting" — both MUST derive via
 * `newestTextMessageAuthorKind` so the two views can't disagree.
 */
export const CONVERSATION_USER_ROW_STATES = ["resolved", "waiting_on_you", "waiting_on_team"];
/**
 * Author kind of the newest TEXT message (system_notes are lifecycle noise, never a "reply").
 * Messages may be passed in either chronological order — newest is taken by `created_at`.
 */
export function newestTextMessageAuthorKind(messages) {
    let newest = null;
    for (const message of messages) {
        if (message.kind !== "text")
            continue;
        const createdAtMs = new RooDate(message.created_at).getTime();
        if (!newest || createdAtMs > newest.createdAtMs) {
            newest = { author_kind: message.author_kind, createdAtMs };
        }
    }
    return newest ? newest.author_kind : null;
}
export function deriveUserConversationRowState(p) {
    if (p.status === "resolved")
        return "resolved";
    // Team/bot spoke last → the ball is in the user's court; user spoke last (or nothing but
    // system notes yet) → we owe them a reply.
    return p.newestTextAuthorKind === "admin" || p.newestTextAuthorKind === "bot" ? "waiting_on_you" : "waiting_on_team";
}
/**
 * Is this thread one a HUMAN owes the next reply on? True only where the bot is silent, so a
 * user message means the TEAM is who's holding things up.
 *
 * ⚠ Deliberately NARROWER than the admin inbox bucket, which also includes
 * `open ∧ human_requested` (escalation v2 — the user asked for a person, the bot keeps
 * answering). Those threads are excluded here because the bot replies within seconds and
 * `postConversationEscalated` already paged for them; including them would notify on every
 * message of a bot-handled conversation.
 */
export function isHumanHandledConversation(p) {
    return p.status === "open" && (p.owner === "human" || p.owner === "queued_human");
}
/**
 * Should a landing USER message page the team? The gate behind the "user replied" Slack post.
 *
 * Two conditions, and the second is the interesting one: we notify on the TRANSITION into
 * "the team is now the one who owes a reply", not on each message. A user firing off three
 * messages in a row pages once. This is derived from the transcript (`priorNewestTextAuthorKind`
 * = the newest TEXT author BEFORE this message landed) rather than from stored state, so it needs
 * no column, no cooldown, and is restart-safe by construction.
 *
 * `null` (no text messages yet, only system notes) counts as a transition — the user speaking
 * first into a human-owned thread is exactly the case we want to hear about.
 */
export function shouldNotifyUserAwaitingReply(p) {
    return isHumanHandledConversation(p) && p.priorNewestTextAuthorKind !== "user";
}
/**
 * Entry-context blob captured by mobile on conversation CREATE and on each REOPEN — where the
 * user was when they reached for support (route, flow position, entry surface). Stored as a
 * capped list (newest last) in `conversation.context_raw`; surfaced on the admin thread view
 * and in the bot's context. Every field but the stamps is best-effort optional, mirroring
 * `feedbackMetadataSchema` (the capture helper is shared).
 */
export const CONVERSATION_ENTRY_SURFACES = ["support-tab", "chrome-help-button"];
export const conversationContextEntrySchema = type({
    capturedAt: "string", // ISO timestamp (client clock; ordering within the list is by append)
    trigger: "'create' | 'reopen'",
    "entrySurface?": "'support-tab' | 'chrome-help-button'",
    "route?": "string",
    "routeHistory?": "string[]",
    // Same shape as `feedbackMetadataSchema.flowLocation` (keys renamed from `step*` 2026-08-19,
    // ADR: flow-terminology; existing rows migrated by roo-db `rename_flow_location_keys`).
    "flowLocation?": {
        pageLid: "string",
        pageIndex: "number",
        pageCount: "number",
        "segment?": "number",
        flowId: "string",
    },
    "appVersion?": "string",
    "otaVersion?": "string",
    "demoMode?": "boolean",
});
export const conversationContextEntriesSchema = conversationContextEntrySchema.array();
/** Cap on stored context entries — oldest dropped first. Create + a few reopens is plenty. */
export const CONVERSATION_CONTEXT_ENTRIES_CAP = 10;
/**
 * Help-content refs sent in a message by an admin (article search + send) or the bot
 * (`suggest_help_article` tool) — `conversation_message.article_refs_raw`. Structured refs,
 * never raw URLs: they survive renames, render as tappable cards opening HelpModal, and are
 * validated server-side against the corpus slug index (articles/faq/roopedia/videos — the
 * same corpus user help search uses). A client whose bundled corpus lacks the slug renders an
 * "update the app" card instead (stale-OTA fallback).
 */
export const conversationArticleRefsSchema = type({
    /** Page slug ("docs/articles/…") — or the videoKey for kind "video". */
    slug: "string",
    // Kinds mirror the help-search collections (helpSearchCore) + video.
    kind: "'articles' | 'faq' | 'roopedia' | 'video'",
    title: "string",
}).array();
/**
 * Quick replies (bot-offered tap-able answers, `conversation_message.quick_replies_raw`): 2–4
 * short labels rendered as chips under the NEWEST bot message; a tap sends the label as a normal
 * user message (pure sugar over typing — the composer is never disabled). Offered by the bot's
 * offer_quick_replies tool.
 */
export const conversationQuickRepliesSchema = type("string[]");
/**
 * The closed set of account actions the bot may offer as tap-able cards
 * (`conversation_message.account_actions_raw`, the `offer_account_actions` tool).
 *
 * ⚠ **A CLOSED ENUM, never free text, and that is the safety property.** The bot cannot author an
 * action; it can only pick from this list, so the worst a hallucinated tool call can produce is an
 * unexpected button — never an arbitrary instruction. The action does nothing on its own: it routes
 * into a flow the user must still confirm (deletion keeps its exit survey and its type-"delete"
 * destructive confirm), so the card is an entry point, not a trigger.
 *
 * - `delete_account` — opens the real deletion flow on the device.
 *
 * ⚠ **It is a one-member enum today and the plural machinery around it is deliberate.** A
 * `leave_setup` action shipped alongside it and was removed on 2026-08-26 (Kevin's call — not a
 * defect; it is re-addable when wanted). Everything that makes a SET work is intact — the array
 * payload, `normalizeAccountActions`, `parseAccountActions`, the card strip — so adding the next
 * action is one entry here plus its card branch, not a reshaping. Do NOT "simplify" the array to a
 * single optional field.
 */
export const CONVERSATION_ACCOUNT_ACTIONS = ["delete_account"];
// jsonb-expand-safe(ConversationMessage.account_actions_raw): narrowing the union from
// {leave_setup, delete_account} to {delete_account} (2026-08-26) is handled on READ, not by a
// migration. Every consumer filters the stored array through the closed enum first — the server's
// `normalizeAccountActions` and mobile's `parseAccountActions`, both of which drop unknown values —
// so a row persisted while `leave_setup` shipped renders the remaining actions and nothing for it.
// Pinned by a regression case in each of those two test suites. (In practice the column shipped
// nowhere, so no such row exists outside a dev database.)
export const conversationAccountActionsSchema = type("'delete_account'").array();
/**
 * Post-resolve CSAT ("Did we get your problem solved?") — rates the RESOLUTION outcome, asked
 * once per thread regardless of who resolved it (bot or human). A `not_solved` answer may carry
 * an optional free-text comment ("what's still unsolved").
 */
export const CONVERSATION_RESOLUTION_RATINGS = ["solved", "not_solved"];
/**
 * Admin-inbox buckets. SHARED-inbox model (no per-admin claiming as a workflow concept):
 * `inbox` = everything on the human side (owner queued_human OR human) in one list ·
 * `bot` = healthy bot chats (on-demand monitoring) · `resolved` = history. The owner state
 * machine underneath is unchanged — an admin reply still silences the bot (implicit claim);
 * "Hand back to the bot" (release) is the one explicit owner affordance left in the UI.
 */
export const CONVERSATION_INBOX_BUCKETS = ["inbox", "bot", "resolved"];
/**
 * Which admin-inbox bucket a single conversation sits in — the row-level twin of the `where`
 * clauses in the server's `s_conversation.listForAdmin`. A list that isn't bucket-scoped (a user's
 * conversation history) needs it to open each thread in the bucket the thread view's `j`/`k`/Esc
 * actually walk. ⚠ Keep the two in step; `conversationEndpoints.test.ts` asserts every row the
 * admin list returns for bucket B derives B.
 */
export function conversationInboxBucketFor(p) {
    if (p.status === "resolved")
        return "resolved";
    return p.owner === "bot" && !p.humanRequested ? "bot" : "inbox";
}
/**
 * The VISIBLE resolve divider ("Kevin closed the conversation"), shown in both clients under
 * the multi-conversation model. `resolverDisplayName` is the admin's display name, the bot
 * name, or "The Roo team" fallback; the idle sweep passes "Roo" phrasing via its own note.
 *
 * `humanRequestWasPending` appends the cancellation clarification — the deterministic backstop
 * for the "the bot promised a human, then closed the thread" gap. It only fires when the request
 * is STILL outstanding at close time: an admin who engages clears `human_requested_at` (claim on
 * reply), so a teammate who actually had the conversation closes it with the plain divider. The
 * line therefore only appears on a genuine cancellation (bot/user close, or an admin closing it
 * offhand without ever replying).
 */
export function buildResolvedNoteBody(p) {
    const base = `${p.resolverDisplayName} closed the conversation`;
    if (!p.humanRequestWasPending)
        return base;
    return `${base}. Your request for a teammate was canceled — reply here if you still need one.`;
}
/** The bot's author_id on every bot-authored row (author_kind: "bot"). */
export const CONVERSATION_BOT_ID = "roo-assistant";
/** The user-facing name of the bot. UI must always label it clearly as AI vs a human teammate. */
export const CONVERSATION_BOT_DISPLAY_NAME = "Roo Assistant (AI)";
/**
 * The system_note appended when an admin RELEASES a claimed thread back to the bot — the
 * symmetric twin of the claim's handoff_notice. Names the bot explicitly (labeling is a
 * safety contract: the next reply is AI again, and the user must never have to guess).
 */
export const HANDED_BACK_NOTE_BODY = `This conversation was passed back to ${CONVERSATION_BOT_DISPLAY_NAME}`;
// ---------------------------------------------------------------------------
// RTDB transport — single source of truth for the two node paths shared by the server (writer,
// Admin SDK) and mobile (reader, open-read rules). RTDB is a doorbell + an ephemeral stream node,
// NEVER the durable conversation: content lives in Postgres and is delivered over HTTP.
// ---------------------------------------------------------------------------
/**
 * Doorbell node: `{updatedAtMs}` only — a non-sensitive pull-trigger telling the user's devices
 * "your conversation state changed; refetch over HTTP". Keyed by userId like the other
 * operational nodes (budget sync, plaid link status).
 */
export function getConversationDoorbellPath(p) {
    return `/conversation-doorbell/${p.userId}`;
}
/**
 * Ephemeral per-turn stream node. The streamId is 128-bit random — the path IS the capability
 * (that's WHY it's random-keyed, not conversationId-keyed: reply text transits here, so its
 * protection is unguessability, same model as plaid-link-session-status). Deleted at turn end;
 * orphans are swept by cron.
 */
export function getChatStreamPath(p) {
    return `/chat-stream/${p.streamId}`;
}
/**
 * Human-typing node: `{untilMs}` only — an ephemeral "a teammate is typing" presence ping,
 * written by the server when an admin types in the thread composer (throttled by the SPA) and
 * read by mobile while THAT conversation is on screen. Self-expiring by READ-time check (typing
 * iff untilMs > now) — no cleanup job; the next ping simply overwrites. Keyed by CONVERSATION
 * (multi-open: a user can watch one of several threads — a user-keyed node couldn't say which
 * thread the teammate is typing in). Carries no content, same non-sensitive posture as the
 * doorbell. Mirrors `getConversationUserTypingPath` below.
 */
export function getConversationTypingPath(p) {
    return `/conversation-typing/${p.conversationId}`;
}
/** How long ONE ping keeps the indicator alive — the SPA re-pings while typing continues. */
export const CONVERSATION_TYPING_TTL_MS = 6000;
/**
 * Admin doorbell: `{updatedAtMs}` only — ONE global node (the inbox is shared) bumped on any
 * user message or escalation, so the portal's 5s polls become event-driven refetches. Same
 * non-sensitive pull-trigger posture as the per-user doorbell.
 */
export function getConversationAdminDoorbellPath() {
    return `/conversation-admin-doorbell`;
}
/**
 * The mirror direction — USER is typing, shown in the admin portal's thread view. Keyed by
 * conversationId (the admin watches a thread; any admin viewing it sees the dots). Same
 * `{untilMs}` self-expiring contract as the admin-typing node above.
 */
export function getConversationUserTypingPath(p) {
    return `/conversation-user-typing/${p.conversationId}`;
}
//# sourceMappingURL=conversation.models.js.map